import { IconAddPhoto } from '../../../svg';
import { Button } from '../../Buttons';
import React from 'react';
import { useTrans } from '../../../../services/i18n';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Grid, Slider } from '@material-ui/core';
import { ModalDialog } from '../../Dialogs/ModalDialog';
import Typography from '../../Typography';
import getCroppedImg from './utils';
import { IconZoomIn, IconZoomOut } from '../../../../components/svg/Icons';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingRight: 0,
  },
  avatarCover: {
    width: '100%',
    height: 'auto',
    overflow: 'hidden',
    aspectRatio: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    borderRadius: 8,
    marginBottom: 10,
    backgroundColor: theme.palette.gray.background,
    [theme.breakpoints.down('sm')]: {
      height: 158,
      width: 158,
    },
  },
  label: {
    marginBottom: 5,
  },
  avatarImage: {
    width: '100%',
    height: 'auto',
  },
  controls: {
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  previewWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3, 1, 1),
  },
  previewContent: {
    width: '100%',
  },
  previewStage: {
    width: '100%',
    minHeight: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fc',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: 95,
    objectFit: 'contain',
    display: 'block',
  },
  previewDivider: {
    width: '100%',
    height: 4,
    backgroundColor: '#d9dde7',
    borderRadius: 999,
    margin: theme.spacing(0, 0, 2.5),
  },
  previewHelpText: {
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    fontSize: '0.95rem',
  },
  previewActions: {
    width: '100%',
    alignItems: 'center',
  },
}));

const CustomSlider = withStyles({
  rail: {
    backgroundColor: '#4E59781F',
  },
  thumb: {
    width: '1.25rem',
    height: '1.25rem',
    marginTop: '-0.56rem',
  },
})(Slider);

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

export default function UploadAvatar({ pending = false, handleSetAvatar, settings, handleRemoveAvatar }) {
  const { trans } = useTrans();
  const classes = useStyles();
  const { avatarId, avatarUrl } = settings;

  const [imgSrc, setImgSrc] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [modalStep, setModalStep] = React.useState('edit');
  const imgRef = React.useRef(null);
  const [crop, setCrop] = React.useState();
  const [completedCrop, setCompletedCrop] = React.useState();
  const [scale, setScale] = React.useState(1);
  const [rotate] = React.useState(0);
  const [aspect] = React.useState(4 / 3);
  const [fileError, setFileError] = React.useState('');
  const [previewImage, setPreviewImage] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState('');

  const inputFile = React.useRef(null);

  const clearPreview = React.useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setPreviewImage(null);
  }, [previewUrl]);

  const resetModalState = React.useCallback(() => {
    clearPreview();
    setOpen(false);
    setModalStep('edit');
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setScale(1);

    if (inputFile.current) {
      inputFile.current.value = '';
    }
  }, [clearPreview]);

  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const onImageLoad = (e) => {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  };

  const handleChange = (event, newValue) => {
    setScale(newValue);
  };

  const closeModal = () => {
    resetModalState();
  };

  const onSelectFile = () => {
    inputFile.current?.click();
  };

  const onChangeFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setFileError(trans('invalid-file-type'));
        e.target.value = '';
        return;
      }

      setFileError('');
      clearPreview();
      setModalStep('edit');
      setScale(1);
      setCrop(undefined);

      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(file);

      setOpen(true);
    }
  };

  const buildPreviewImage = async () => {
    try {
      const image = await getCroppedImg(imgSrc, completedCrop, scale, rotate);

      clearPreview();

      const nextPreviewUrl = URL.createObjectURL(image);
      setPreviewImage(image);
      setPreviewUrl(nextPreviewUrl);
      setModalStep('preview');
    } catch (error) {
      console.error(error);
    }
  };

  const confirmUploadImage = async () => {
    try {
      const image = previewImage || (await getCroppedImg(imgSrc, completedCrop, scale, rotate));

      const data = {
        currentTarget: {
          files: [new File([image], `profile_image.${image.type.split('/').pop()}`)],
        },
      };

      await handleSetAvatar(data);
      resetModalState();
    } catch (error) {
      console.error(error);
    }
  };

  const backToEdit = () => {
    clearPreview();
    setModalStep('edit');
  };

  const renderEditActions = (
    <Grid container spacing={2} justifyContent="space-between">
      <Grid item style={{ width: '33%' }} />
      <Grid item style={{ width: '33%' }}>
        <Grid container style={{ width: '100%' }} spacing={2} justifyContent="space-between">
          <Grid item>
            <IconZoomOut />
          </Grid>
          <Grid item style={{ flexGrow: 1 }}>
            <CustomSlider value={scale} onChange={handleChange} step={0.1} max={3} min={0} track={false} color="gray" />
          </Grid>
          <Grid item>
            <IconZoomIn />
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={{ width: '33%', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="success" onClick={buildPreviewImage}>
          Continue
        </Button>
      </Grid>
    </Grid>
  );

  const renderPreviewActions = (
    <Grid container justifyContent="space-between" className={classes.previewActions}>
      <Grid item>
        <Button variant="contained" color="success" onClick={backToEdit}>
          Back
        </Button>
      </Grid>
      <Grid item>
        <Button pending={pending} variant="contained" color="success" onClick={confirmUploadImage}>
          Confirm
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Typography weight="500" className={classes.label} fontFamily="lexend" color="label" variant="body2">
        {trans('logo')}
      </Typography>

      <div className={classes.avatarCover}>
        {avatarId && avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className={classes.avatarImage} />
        ) : (
          <IconAddPhoto />
        )}
      </div>

      <Grid container item xs={12} alignItems="center" className={classes.controls}>
        <Button
          pending={pending}
          id="upload-avatar-btn"
          variant="text"
          size="small"
          color="primary"
          onClick={onSelectFile}
        >
          {trans(avatarId ? 'change' : 'upload')}
          <input
            ref={inputFile}
            id="input-upload-avatar"
            type="file"
            accept=".png,.jpg,.jpeg"
            style={{ display: 'none' }}
            onChange={onChangeFile}
          />
        </Button>

        {avatarId && (
          <Button id="remove-avatar" variant="text" size="small" onClick={handleRemoveAvatar} color="primary">
            {trans('remove')}
          </Button>
        )}
      </Grid>

      {fileError && (
        <Typography variant="body2" style={{ color: '#f44336', marginTop: 4, fontSize: 12 }}>
          {fileError}
        </Typography>
      )}

      <ModalDialog
        open={open}
        onClose={closeModal}
        title={modalStep === 'preview' ? 'Confirm Logo Edits' : trans('edit-image-profile')}
        actions={modalStep === 'preview' ? renderPreviewActions : renderEditActions}
      >
        {modalStep === 'edit' ? (
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              style={{
                position: 'relative',
                width: '42.5rem',
                height: 'fit-content',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {!!imgSrc && (
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspect}
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              )}
            </Grid>
          </Grid>
        ) : (
          <Grid container>
            <Grid item xs={12} className={classes.previewWrapper}>
              <div className={classes.previewContent}>
                <div className={classes.previewStage}>
                  {!!previewUrl && <img src={previewUrl} alt="Logo preview" className={classes.previewImage} />}
                </div>

                <div className={classes.previewDivider} />

                <Typography variant="body2" className={classes.previewHelpText}>
                  Click Confirm to submit your edits.
                  <br />
                  Click Back to make changes.
                </Typography>
              </div>
            </Grid>
          </Grid>
        )}
      </ModalDialog>
    </div>
  );
}
